import { Game } from "./game";
import { DataMiddleware } from "./data-middleware";
import { ShaderMiddleware } from "./shader-middleware";
import { AnimationMiddleware } from "./animation-middleware";
import { OceanMiddleware } from "./ocean-middleware";
import { WaterMiddleware } from "./water-middleware";
import { WeatherMiddleware } from "./weather-middleware";
import { ZoneEffectsMiddleware } from "./zone-effects-middleware";
import { NetworkingMiddleware } from "./networking-middleware";
import { GUIMiddleware } from "./gui-middleware";
import { StateMiddleware } from "./state-middleware";
import type { GameContext, GameMiddleware } from "./types";

/**
 * Composite middleware that bundles all ROSE Online game features.
 *
 * In RoseOnlineEditor.onInit():
 *   this.use(new RoseOnlineMiddleware());
 *
 * In client core.ts:
 *   game.use(new RoseOnlineMiddleware());
 *
 * Individual features can be retrieved after init via:
 *   editor.getMiddleware(RoseOnlineMiddleware)?.inner.weather?.getManager()
 */
export class RoseOnlineMiddleware implements GameMiddleware {
    readonly inner = new Game()
        .use(new DataMiddleware())
        .use(new ShaderMiddleware())
        .use(new AnimationMiddleware())
        .use(new OceanMiddleware())
        .use(new WaterMiddleware())
        .use(new WeatherMiddleware())
        .use(new ZoneEffectsMiddleware())
        .use(new NetworkingMiddleware())
        .use(new GUIMiddleware())
        .use(new StateMiddleware());

    async init(ctx: GameContext): Promise<void> {
        await this.inner.init(ctx);
    }

    update(delta: number): void {
        this.inner.update(delta);
    }

    destroy(): void {
        this.inner.destroy();
    }

    onModeChange(mode: "editor" | "preview"): void {
        this.inner.onModeChange(mode);
    }
}
